import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { URI } from '@fiverr-private/futile';
import { getContext } from '@fiverr-private/fiverr_context';
import { OPEN_GIG_SAME_TAB_TARGET, URL_TARGET } from '../constants';
import { ROLLOUTS } from '../../../constants/experiments';
import { useGigListingsContext } from '../../../GigCardListings/context/GigListingsContext';
import { useCleanHref } from '../../../hooks/useCleanHref/useCleanHref';
import { useCardContext } from '../../../GigCardListings/context/CardContext';
import { useRolloutsContext } from '../../../GigCardListings/context/RolloutsContext/RolloutsContext';

const ContextualLink = ({
    href,
    title = '',
    rel = '',
    onMouseDown,
    onMouseEnter,
    onMouseLeave,
    className,
    target,
    ariaLabel,
    auctionId = null,
    children,
}) => {
    const { urlTarget } = useGigListingsContext();
    const { impressionId } = useCardContext();
    const { isMobile } = getContext();
    const { rollouts } = useRolloutsContext();
    const isCleanGigCardUrlActive = rollouts?.[ROLLOUTS.CROWS_CLEAN_GIG_CARD_URL];
    const linkTarget = isMobile ? OPEN_GIG_SAME_TAB_TARGET : urlTarget || target || OPEN_GIG_SAME_TAB_TARGET;
    const contextualUrl = new URI(href).addParams({
        imp_id: impressionId,
    }).href;
    const displayHref = isCleanGigCardUrlActive ? new URI(contextualUrl).removeParams().href : contextualUrl;

    const fullUrl = useMemo(
        () => (auctionId ? new URI(contextualUrl).addParams({ ad_key: auctionId }).href : contextualUrl),
        [contextualUrl, auctionId]
    );

    const handleClick = useCallback(
        (e) => {
            e.preventDefault();
            window.open(fullUrl, isMobile ? URL_TARGET.self : URL_TARGET.blank);
        },
        [fullUrl, isMobile]
    );

    const { href: cleanLinkHref, handlers: cleanLinkHandlers } = useCleanHref({
        cleanHref: displayHref,
        fullUrl,
        onMouseDown,
        onClick: handleClick,
    });

    // Control group keeps the previous behavior untouched
    const linkProps = isCleanGigCardUrlActive
        ? { href: cleanLinkHref, ...cleanLinkHandlers }
        : { href: displayHref, onMouseDown, onClick: handleClick };

    return (
        <a
            target={linkTarget}
            aria-label={ariaLabel}
            className={className}
            title={title}
            rel={rel}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            {...linkProps}
        >
            {children}
        </a>
    );
};

ContextualLink.propTypes = {
    children: PropTypes.any,
    className: PropTypes.any,
    onMouseDown: PropTypes.func,
    onMouseEnter: PropTypes.func,
    onMouseLeave: PropTypes.func,
    rel: PropTypes.any,
    title: PropTypes.any,
    href: PropTypes.any,
    target: PropTypes.any,
    ariaLabel: PropTypes.string,
    auctionId: PropTypes.string,
};

export default ContextualLink;
